// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/styles';
import { get } from 'lodash';
import { isEqual } from 'lodash';
import { push } from 'connected-react-router';
import { array, arrayOf, bool, func, shape, string } from 'prop-types';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import find from 'lodash/find';
import {
  FormActionButtons, TabPanel, TabsContainer,
} from '../../../components';
import { getTabIndex, getUserRole } from '../../../utils';
import {
  accessArrayToRolesMap,
  deduceUserType,
} from './utils';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';

import {
  getIsError,
  getErrors,
  getIsLoading,
  getValidationError,
  getUserUpdateResult,
  getCreatedUser,
  getUserCreateSuccess,
  getUserDetails,
  getResendActivationEmailResult,
  getUserDeleteResult,
  getUserRevokeAccessResult,
  getUsersFetchResult,
} from '../../../reducers/user';

import {
  getUserDetails as getUserContext,
  getUserActiveOrg,
  getActiveUserContext,
} from '../../../reducers/portalConfig';
import DetailsHeader from './Header';
import DetailsContainer from '../../details';
import styles from './styles';
import AddUser from './AddUser';
import EditUser from './EditUser';
import { fetchAuthSchemes } from '../../../actions/authSchemes';
import { fetchDisableLocalLoginsFeatureFlag } from '../../../actions/portalConfig';
import { getAuthSchemesList } from '../../../reducers/authSchemes';
import { getLocalLoginsDisabled } from '../../../reducers/portalConfig';
import {
  ALERT_ERROR,
  ROLE_ORG_ADMIN,
  ROLE_ORG_ADMIN_UUID,
  ROLE_PORTAL_ADMIN,
  ROLE_PORTAL_ADMIN_UUID,
  FAIL,
  SUCCESS,
  ALERT_SUCCESS,
  USER_STATUS_DISABLED,
  USER_STATUS_ENABLED,
  USER_STATUS_REGISTRATION_PENDING_APPROVAL,
  USER_TYPE_GLOBAL,
  USER_TYPE_ORG,
} from '../../../constants';
import {
  createUser,
  fetchUser,
  updateUser,
  resetApp,
  resetUpdateNotification,
  deleteUser,
  resendActivationEmail,
} from '../../../actions/user';
import OrganizationAccess from './OrganizationAccess';

const getTabItems = (globalUser) => [
  {
    id: 'details',
    tabId: 'user-details-tab',
    label: getI18nFormattedMessage('label.details'),
    visible: true,
  },
  {
    id: 'organizations',
    tabId: 'user-details-orgs-tab',
    label: getI18nFormattedMessage('label.organizations'),
    visible: true,
    disabled: globalUser,
  },
];

const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

export const UserDetails = (props) => {
  const {
    activeUserContext,
    createdUser,
    isLoading,
    isError,
    classes,
    authSchemesList = [],
    localLoginsDisabled,
    fetchUser,
    userCreateSuccess,
    userDetails,
    userValidationError,
    userContext,
    userActiveOrg,
    updateUser,
    userUpdateResult,
    resendActivationEmailResult,
    resetUpdateNotification,
    userDeleteResult,
    userRevokeResult,
    usersFetchResult,
    history,
  } = props;

  const userUuid = get(props, 'match.params.userUuid');
  const pageHeaderRef = useRef(null);
  const intl = getI18n(useIntl());
  const [authSchemeListForCreate, setAuthSchemeListForCreate] = useState([]);
  const [defaultAuthScheme, setDefaultAuthScheme] = useState('DEFAULT');
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [currentUserOrg, setCurrentUserOrg] = useState('');
  const [notifyUser, setNotifyUser] = useState(false);
  const [userFormObject, setUserFormObject] = useState({});
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [unsavedDialog, setUnsavedDialog] = useState(false);
  const [saveClicked, setSaveClicked] = useState(false);
  const { userType } = userFormObject;
  const tabItems = getTabItems(userType === USER_TYPE_GLOBAL);
  const tabs = getTabs(tabItems);
  const defaultIndex = getTabIndex(tabs, props.match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);
  const [defaultAddUser, setDefaultAddUser] = useState({});

  // -ve test cases
  // self edit, org admin editing users from other auth schemes,
  // org admin editing user other org users
  useEffect(() => {
    if(authSchemesList && authSchemesList.length > 0) {
      let authSchemes= authSchemesList.filter(scheme => get(scheme, 'authMethod') === 'DEFAULT'
                          || (get(scheme, 'authMethod') === 'SAML' && get(scheme, 'authznType') === 'PORTAL'))
                          .map(scheme => { return {
                            'uuid': scheme.authMethod === 'DEFAULT' ? 'DEFAULT' : scheme.uuid,
                            'text': scheme.name,
                            'authMethod': scheme.authMethod,
                            'authznType': scheme.authznType,
                            };
                          });
      if(localLoginsDisabled) {
        authSchemes = authSchemes.filter(scheme => scheme.authMethod !== 'DEFAULT');
        if (authSchemes.length > 0) {
          setDefaultAuthScheme(authSchemes[0].uuid);
        }
      }
      setAuthSchemeListForCreate(authSchemes);
    }
  }, [authSchemesList, localLoginsDisabled]);

  // We cannot use authSchemeListForCreate here, as we have to
  // render idp authorized users
  const authSchemesListForEdit = authSchemesList.map(scheme => ({
      'uuid': scheme.authMethod === 'DEFAULT' ? 'DEFAULT' : scheme.uuid,
      'text': scheme.name,
      'authMethod': scheme.authMethod,
      'authznType': scheme.authznType,
  }));

  useEffect(() => {
    if (userDetails) {
      setUserFormObject({
        ...userDetails,
        userType: deduceUserType(userDetails.access),
      });
    }
  }, [userDetails]);

  useEffect(() => {
    if(userUuid) {
      fetchUser(userUuid);
    }
  }, []);

  useEffect(() => {
    const defaultIndex = getTabIndex(tabs, props.match);
     setTabIndex(defaultIndex);
     setCurrentTab(tabs[defaultIndex].tabId);
 }, [props.match]);

  useEffect(() => {
    if(userCreateSuccess) {
      const { uuid } = createdUser;
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.created'));
      setTimeout(() => {
        notifyMessages('', '');
        window.location.href = `/admin/console/users/details/${uuid}`;
      }, 2500);
    }
  }, [userCreateSuccess]);

  // check disable local login
  const getDefaultAddUser = (isAdmin, userContext) => {
    const user = {
      status: 'NO_STATUS',
    };
    if (isAdmin) {
      user.authConfigUuid = defaultAuthScheme;
      user.userType = USER_TYPE_GLOBAL;
      user.access = [{
        roleUuid: ROLE_PORTAL_ADMIN_UUID,
      }];
    } else {
      user.authConfigUuid = get(userContext, 'authConfigUuid');
      user.userType = USER_TYPE_ORG;
      user.access = [{
        roleUuid: ROLE_ORG_ADMIN_UUID,
        orgUuid: userContext.organizationUuid,
      }];
    }
    return user;
  };

  useEffect(() => {
    if(userContext) {
      const loggedInUserRole = getUserRole(userContext);
      const isAdmin = loggedInUserRole === ROLE_PORTAL_ADMIN;
      const isOrgAdmin = loggedInUserRole === ROLE_ORG_ADMIN;
      if(isOrgAdmin || isAdmin) {
        setCurrentUserRole(loggedInUserRole);
        const user = getDefaultAddUser(isAdmin, userContext);
        setDefaultAddUser(user);
        setUserFormObject(JSON.parse(JSON.stringify(user)));
        if(isAdmin) {
          props.fetchAuthSchemes();
          props.fetchDisableLocalLoginsFeatureFlag();
        } else {
          setCurrentUserOrg(userContext.organizationUuid);
        }
      } else {
        props.push('/404');
      }
    }
  }, [userContext]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const updateUserFormObjectMultipeFields = (updatedFields) => {
    setUserFormObject({
      ...userFormObject,
      ...updatedFields,
    });
  };

  const selectedAuthznMethod =
    get(find(authSchemesList, authScheme => authScheme.uuid === userFormObject.authConfigUuid ||
                      authScheme.authMethod === userFormObject.authConfigUuid), 'authznType', '');

  const canEditUser = selectedAuthznMethod !== 'IDP';

  const updateUserFormObject = (field, value) =>{
    if (field === 'userType') {
      let newAccess = [];
      if (value === USER_TYPE_GLOBAL) {
        newAccess = [{ roleUuid: ROLE_PORTAL_ADMIN_UUID }];
      }
      setUserFormObject({
        ...userFormObject,
        access: newAccess,
        [field]:value,
      });
    } else if (field === 'username') {
      setUserFormObject({
        ...userFormObject,
        userCanBeAdded: false,
        userIsGuestOrOtherOrgUser: false,
        userToBeAdded: {},
        [field]:value,
      });
    } else {
      setUserFormObject({
        ...userFormObject,
        [field]:value,
      });
    }
  };

  const isUserObjectDirty = () => {
    if(!userUuid) {
      return (
        userFormObject.authConfigUuid !== defaultAddUser.authConfigUuid ||
        userFormObject.userType !== defaultAddUser.userType ||
        userFormObject.userRole !== defaultAddUser.userRole ||
        userFormObject.firstName ||
        userFormObject.lastName ||
        userFormObject.email
      );
    } else {
      const { access: existingAccess = [] } = userDetails || {};
      const existingRolesMap = accessArrayToRolesMap(existingAccess);

      const { access: updatedAccess = [] } = userFormObject || {};
      const updatedRolesMap = accessArrayToRolesMap(updatedAccess);

      const userTypeFromDetails = deduceUserType(existingAccess);

      return userFormObject && userDetails && (
        userFormObject.userType !== userTypeFromDetails ||
        !isEqual(existingRolesMap, updatedRolesMap) ||
        userFormObject.firstName !== userDetails.firstName ||
        userFormObject.lastName !== userDetails.lastName ||
        userFormObject.email !== userDetails.email
      );
    }
  };

  const isSaveEnabled = () => {
    const isAdmin = currentUserRole === ROLE_PORTAL_ADMIN;
    if(!userUuid) {
      if (isAdmin) {
        if (userFormObject.authConfigUuid === 'DEFAULT') {
          if (localLoginsDisabled) {
            return false;
          } else if (userType === USER_TYPE_GLOBAL) {
            return userFormObject.email;
          } else if (userType === USER_TYPE_ORG) {
            return userFormObject.email && (userFormObject.access.length > 0);
          }
        } else {
          return userFormObject.userCanBeAdded && (userFormObject.access.length > 0);
        }
      } else {
        if (userFormObject.authConfigUuid === 'DEFAULT') {
          return userFormObject.email;
        } else {
          return userFormObject.userCanBeAdded;
        }
      }
    } else {
      if (isAdmin) {
        if (userFormObject.authConfigUuid === 'DEFAULT') {
          if (userType === USER_TYPE_GLOBAL) {
            return userFormObject.email && isUserObjectDirty();
          } else if (userType === USER_TYPE_ORG) {
            return userFormObject.email && isUserObjectDirty() &&
              (userFormObject.access.length > 0);
          }
        } else if (!canEditUser){
          return false;
        } else {
          return isUserObjectDirty() && (userFormObject.access.length > 0);
        }
      } else {
        if (userFormObject.authConfigUuid === 'DEFAULT') {
          return userFormObject.email;
        } else if (!canEditUser){
          return false;
        } else {
          return isUserObjectDirty();
        }
      }
    }
    return true;
  };

  const renderNotifyCheckBoxOnFooter = (<FormControlLabel
    classes={{
      root: classes.defaultCheckboxContainer,
    }}
    control={
      <Checkbox
        classes={{ root: classes.iconRoot }}
        defaultChecked={notifyUser}
        size="small"
        checked={notifyUser}
        onChange={(event) => { setNotifyUser(event.target.checked); }}
      />
    }
    label={intl.getI18nMessage('label.user.details.notify.checkbox')}
  />);

  const handleTabChange = (e, tabIndex) => {
    setTabIndex(tabIndex);
    setCurrentTab(tabs[tabIndex].tabId);
    if(userUuid)
      history.push(`/admin/console/users/details/${userUuid}/${tabs[tabIndex].id}`)
  };

  const saveUserDetails = () => {
    setSaveClicked(true);
    notifyMessages('', '');
    const userDomainObject = {
      firstName: userFormObject.firstName,
      lastName: userFormObject.lastName,
      email: userFormObject.email,
      authConfigUuid: userFormObject.authConfigUuid,
      access: userFormObject.access,
    }

    if(!userUuid) {
      userDomainObject.status = 'REGISTRATION_INIT';
      if (userFormObject.authConfigUuid !== 'DEFAULT') {
        userDomainObject.username = userFormObject.username;
      }
      if (userFormObject.userIsGuestOrOtherOrgUser) {
        const { userToBeAdded } = userFormObject;
        userDomainObject.uuid = userToBeAdded.uuid;
        userDomainObject.firstName = userToBeAdded.firstName;
        userDomainObject.lastName = userToBeAdded.lastName;
        userDomainObject.lastName = userToBeAdded.lastName;
        userDomainObject.email = userToBeAdded.email;
        userDomainObject.status = userToBeAdded.status;
        userDomainObject.tenantId = userToBeAdded.tenantId;
        updateUser(userDomainObject);
      } else {
        props.createUser(userDomainObject, notifyUser);
      }
    } else {
      userDomainObject.uuid = userDetails.uuid;
      userDomainObject.status = userDetails.status;
      userDomainObject.username = userDetails.username;
      userDomainObject.tenantId = userDetails.tenantId;
      userDomainObject.businessReportsObjectId = "";
      updateUser(userDomainObject);
    }
  };

  const changeStatus = (currentStatus) => {
    updateUser({
      ...userDetails,
      status: currentStatus === USER_STATUS_DISABLED ? USER_STATUS_ENABLED : USER_STATUS_DISABLED,
    });
  };

  const onCancel = () => {
    if(isUserObjectDirty()) {
      setUnsavedDialog(true);
    } else {
      props.push('/admin/console/users');
    }
  };

  useEffect(() => {
    if(isError) {
      if(userValidationError) {
        notifyMessages(ALERT_ERROR, userValidationError);
      } else if(!userUuid) {
        if (usersFetchResult === FAIL) {
          notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.details.generic.fetch.error'));
        } else {
          notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.details.generic.add.error'));
        }
      }
    }
  }, [isError, usersFetchResult, userValidationError]);

  useEffect(() => {
    if (userUpdateResult === SUCCESS) {
      if (userUuid) {
        notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.details.update.success.message'));
        fetchUser(userUuid);
        setTimeout(() => {
          notifyMessages('', '');
          resetUpdateNotification();
        }, 2500);
      } else {
        const { userToBeAdded } = userFormObject || {};
        const { uuid } = userToBeAdded || {};
        if (uuid) {
          notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.created'));
          setTimeout(() => {
            notifyMessages('', '');
            resetUpdateNotification();
            window.location.href = `/admin/console/users/details/${uuid}`;
          }, 2500);
        }
      }
    } else if (userUpdateResult === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.details.generic.update.error'));
    }
  }, [userUpdateResult]);

  useEffect(() => {
    if (userRevokeResult === SUCCESS) {
      props.push('/admin/console/users');
    } else if (userRevokeResult === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.details.revoke.error'));
    }
  }, [userRevokeResult]);

  useEffect(() => {
    if (userDeleteResult === SUCCESS) {
        props.push('/admin/console/users');
    } else if (userDeleteResult === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.details.delete.error'));
    }
  }, [userDeleteResult]);

  useEffect(() => {
    if (resendActivationEmailResult === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.user.details.resend.email.success'));
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    } else if (resendActivationEmailResult === FAIL) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.user.details.resend.email.error'));
    }
  }, [resendActivationEmailResult]);

  const doRevokeInsteadOfDelete =
    currentUserRole === ROLE_PORTAL_ADMIN ? false : userFormObject.isMultiOrgUser;

  const deleteFn = () => {
    if (doRevokeInsteadOfDelete) {
      updateUser({
        ...userDetails,
        access: [],
      }, true);
    } else {
      props.deleteUser(userUuid);
    }
  };

  const deleteButtonLabel = doRevokeInsteadOfDelete ? intl.getI18nFormattedMessage('label.user.details.orgadmin.delete')
    : intl.getI18nFormattedMessage('label.user.details.admin.delete');

  return (
    <DetailsContainer
      detailsPageId="user-details-page"
      isLoading={isLoading}
      notificationId="user-details-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      notificationContainerClass={classes.notificationContainer}
      hasNotificationIcon={true}
      isActionsEnabled={false}
      pageHeaderRef={pageHeaderRef}
      isDialogOpen={unsavedDialog}
      onDialogClose={() => setUnsavedDialog(false)}
      onDialogSubmit={() => props.push('/admin/console/users')}
      dialogSubmitText={intl.getI18nMessage('label.dialog.unsaved.changes.submit')}
      header={
        <DetailsHeader
          headerId="user-details-header-container"
          title={intl.getI18nMessage('label.user.list.page.title')}
          type="label"
          name={intl.getI18nMessage(`label.user.details.${userUuid?'edit':'add'}.user.title`)}
          helperText={intl.getI18nMessage('label.product.name.help')}
          status={userFormObject.status}
          showDelete={userUuid &&
                      userDetails &&
                      userDetails.status !== USER_STATUS_REGISTRATION_PENDING_APPROVAL}
          changeStatus={changeStatus}
          resendActivationEmail={() => props.resendActivationEmail(userUuid)}
          isAdmin={currentUserRole === ROLE_PORTAL_ADMIN}
          isDefaultAuthScheme={userFormObject.authConfigUuid === 'DEFAULT'}
          deleteButtonLabel={deleteButtonLabel}
          deleteFn={deleteFn}
        />
      }
      tabs={currentUserRole === ROLE_PORTAL_ADMIN &&
        <TabsContainer
          id={'user-details-tab-container'}
          tabValue={tabIndex}
          orientation="horizontal"
          tabItems={tabs}
          handleTabChange={handleTabChange}
        />
      }
      page={
        (<Fragment>
          <TabPanel
            visible={currentTab === 'user-details-tab'}
            id="user-details"
          >
            {!userUuid && <AddUser
              authSchemes={authSchemeListForCreate}
              userFormObject={userFormObject}
              updateUserFormObject={updateUserFormObject}
              updateUserFormObjectMultipeFields={updateUserFormObjectMultipeFields}
              saveClicked={saveClicked}
              currentUserRole={currentUserRole}
              userActiveOrg={userActiveOrg}
              currentUserOrg={currentUserOrg}
              userContext={userContext}
            />}
            {userUuid && <EditUser
              activeUserContext={activeUserContext}
              authSchemes={authSchemesListForEdit}
              userFormObject={userFormObject}
              updateUserFormObject={updateUserFormObject}
              saveClicked={saveClicked}
              currentUserRole={currentUserRole}
              currentUserOrg={currentUserOrg}
              userContext={userContext}
              userDetails={userDetails}
            />}
          </TabPanel>
          {currentUserRole === ROLE_PORTAL_ADMIN &&
            <TabPanel
              visible={currentTab === 'user-details-orgs-tab'}
              id="user-details-orgs"
            >
              <OrganizationAccess
                disableEdit={!canEditUser}
                userContext={userContext}
                userFormObject={userFormObject}
                updateUserFormObject={updateUserFormObject}
              />
            </TabPanel>
          }
       </Fragment>)
      }
      footer={
        <FormActionButtons
          assistText={userFormObject.authConfigUuid === 'DEFAULT' && !userUuid &&
                  renderNotifyCheckBoxOnFooter}
          isContainerFooter
          isDisabled={!isSaveEnabled()}
          nextText={intl.getI18nMessage(`label.user.details.button.${userUuid ? 'save': 'create'}`)}
          onNextClick={saveUserDetails}
          onCancelClick={onCancel}
          id="users-form-buttons"
        />
      }
    />
  );
};

const mapStateToProps = state => ({
  activeUserContext: getActiveUserContext(state),
  createdUser: getCreatedUser(state),
  isLoading: getIsLoading(state),
  isError: getIsError(state),
  pageErrors: getErrors(state),
  userContext: getUserContext(state),
  userActiveOrg: getUserActiveOrg(state),
  authSchemesList: getAuthSchemesList(state),
  localLoginsDisabled: getLocalLoginsDisabled(state),
  userCreateSuccess: getUserCreateSuccess(state),
  userDetails: getUserDetails(state),
  userRevokeResult: getUserRevokeAccessResult(state),
  userUpdateResult: getUserUpdateResult(state),
  userValidationError: getValidationError(state),
  resendActivationEmailResult: getResendActivationEmailResult(state),
  userDeleteResult: getUserDeleteResult(state),
  usersFetchResult: getUsersFetchResult(state),
});

const mapDispatchToProps = {
  fetchAuthSchemes,
  fetchDisableLocalLoginsFeatureFlag,
  push,
  createUser,
  fetchUser,
  updateUser,
  resetApp,
  resendActivationEmail,
  resetUpdateNotification,
  deleteUser,
};

UserDetails.propTypes = {
  activeUserContext: shape({}),
  createdUser: shape({}),
  config: shape({}),
  userContext: shape({}),
  userDetails: shape({}),
  history: shape({}),
  isLoading: bool,
  isError: bool,
  pageErrors: arrayOf(shape({})),
  match: shape({}),
  push: func,
  classes: shape({}),
  fetchDisableLocalLoginsFeatureFlag: func,
  fetchAuthSchemes: func,
  authSchemesList: array,
  localLoginsDisabled: bool,
  userCreateSuccess: bool,
  userRevokeResult: string,
  userValidationError: shape({}),
  userUpdateResult: string,
  resetApp: func,
  resetUpdateNotification: func,
  createUser: func,
  userActiveOrg: shape({}),
  updateUser: func,
  fetchUser: func,
  resendActivationEmail: func,
  resendActivationEmailResult: string,
  userDeleteResult: string,
  usersFetchResult: string,
  deleteUser: func,
};

UserDetails.displayName = 'User Details';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(UserDetails);
